import {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useI18N } from "@src/language/language-context";
import {
  errorSnackbar,
  successSnackbar,
  warningSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { isValidIpAddress } from "@src/cloud-connect/utils/is-valid-ip-address";
import { isValidHostnameOrIpAddress } from "@src/cloud-connect/utils/is-valid-hostname-or-ip-address";
import { getOperationState } from "@src/cloud-connect/utils/resolve-operation-status";
import { RootState } from "@src/reducers/store";
import { updateModalsCloseDrawerModal } from "@src/reducers/your-reducer";
import {
  resetOnboardingState,
  setConnectDataSourceClientCertificate,
  setMachineAvailabilityError,
  setGatewayDetails,
  setGatewayOnboardingStatus,
  setMachineAvailabilityStatus,
  setMachineDetails,
  setMachineSkillsLabel,
  setMachineOnboardingStatus,
  setMachineOnboardingSummary,
} from "@src/reducers/onboarding-slice";
import {
  GatewayOnboardingStatus,
  GatewayOperationError,
  MachineAvailabilityStatus,
  MachineOnboardingStatus,
  MachineOnboardingSummary,
} from "@src/types/gateway-onboarding";
import { GatewayWithMachinesViewStateTypes } from "@src/types/gateways-with-machines";
import { OnboardingStepStatus } from "@src/components/atoms/new-onboarding-stepper/new-onboarding-stepper";
import { checkMachineAvailabilityUseCase } from "@src/cloud-connect/client-configuration/use-cases/check-machine-availability-use-case";
import { Help } from "@src/cloud-connect/help/entities/help";
import { HelpRepository } from "@src/cloud-connect/help/repositories/help-repository";
import navigateTo from "@src/routes/navigator";
import { Route } from "@src/routes/routes";
import { CreateSkillRequestBody, SkillType } from "@src/types/skill-types";
import {
  pollSkillOperationUseCase,
  postCreateSkillUseCase,
} from "@src/cloud-connect/skills/use-cases/post-create-skill-use-case";
import { createMachineForMachineOnboardingUseCase } from "@src/cloud-connect/client-configuration/use-cases/create-machine-for-machine-onboarding-use-case";
import { generateOpcUaClientCertificateUseCase } from "@src/cloud-connect/client-configuration/use-cases/generate-opcua-client-certificate-use-case";
import {
  DetectedMachineSkill,
  extractGatewayOperationError,
} from "@src/cloud-connect/utils/onboarding-operation";
import {
  buildSkillPayload,
  isValidPort,
  pollMachineAvailabilityOperation,
  pollMachineCreationOperation,
} from "./use-add-new-machine-drawer-modal-flow";
import {
  createDefaultOpcUaSettings,
  getMachineDrawerTexts,
  getMachineSkillsTexts,
  getPrefillGateway,
} from "./use-add-new-machine-drawer-modal-texts";

export const useAddNewMachineDrawerModal = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const connectedGatewaysListData = useSelector(
    (state: RootState) => state.gatewaysMachinesSlice.connectedGatewaysListData,
  );

  const onboardingGateway = useSelector(
    (state: RootState) => state.onboardingSlice.onboardingDetails.gateway,
  );

  const machineAvailabilityStatus = useSelector(
    (state: RootState) => state.onboardingSlice.machineAvailabilityStatus,
  );

  const machineAvailabilityError = useSelector(
    (state: RootState) => state.onboardingSlice.machineAvailabilityError,
  );

  const machineSkillsLabel = useSelector(
    (state: RootState) => state.onboardingSlice.machineSkillsLabel,
  );

  const machineOnboardingStatus = useSelector(
    (state: RootState) => state.onboardingSlice.machineOnboardingStatus,
  );

  const machineOnboardingSummary = useSelector(
    (state: RootState) => state.onboardingSlice.machineOnboardingSummary,
  );

  const gatewayOnboardingStatus = useSelector(
    (state: RootState) => state.onboardingSlice.gatewayOnboardingStatus,
  );
  const completedGatewayIdRef = useRef(
    gatewayOnboardingStatus ===
      GatewayOnboardingStatus.GATEWAY_ONBOARDING_COMPLETED
      ? onboardingGateway.gateway_id
      : undefined,
  );

  const onboardingMachine = useSelector(
    (state: RootState) => state.onboardingSlice.onboardingDetails.machine,
  );

  const connectDataSourceClientCertificate = useSelector(
    (state: RootState) =>
      state.onboardingSlice.connectDataSourceClientCertificate,
  );

  const prefillGateway = useMemo(
    (): GatewayWithMachinesViewStateTypes | null =>
      getPrefillGateway(onboardingGateway, connectedGatewaysListData),
    [connectedGatewaysListData, onboardingGateway],
  );
  const [selectedGateway, setSelectedGateway] =
    useState<GatewayWithMachinesViewStateTypes | null>(prefillGateway);
  const [machineName, setMachineName] = useState("");
  const [ipAddress, setIpAddress] = useState("");
  const [createdMachineId, setCreatedMachineId] = useState("");
  const [createdMachineHostname, setCreatedMachineHostname] = useState<
    string | null
  >(null);
  const [isMachineCreated, setIsMachineCreated] = useState(false);
  const [machineCreateIntent, setMachineCreateIntent] = useState<
    "continue" | "finish" | null
  >(null);
  const [pendingMachineCreateOperationId, setPendingMachineCreateOperationId] =
    useState<string | null>(null);
  const [areActionsLocked, setAreActionsLocked] = useState(false);
  const availabilityRequestGenerationRef = useRef(0);
  const machineFlowGenerationRef = useRef(0);
  const machineOperationAbortControllerRef = useRef<AbortController | null>(
    null,
  );
  const [detectedMachineSkills, setDetectedMachineSkills] = useState<
    DetectedMachineSkill[]
  >([]);
  const [isSupportExpanded, setIsSupportExpanded] = useState(false);
  const [isTwinExpanded, setIsTwinExpanded] = useState(false);
  const [vncPort, setVncPort] = useState("");
  const [selectedTwinType, setSelectedTwinType] = useState<
    "OPCUA" | "RPC" | null
  >(null);
  const [twinPort, setTwinPort] = useState("");
  const [rpcDsn, setRpcDsn] = useState("");
  const [isInstallSkillsLoading, setIsInstallSkillsLoading] = useState(false);
  const [isRetryEditingSkills, setIsRetryEditingSkills] = useState(false);
  const [hasSkillOperationFailure, setHasSkillOperationFailure] =
    useState(false);
  const [canRetrySkillSetup, setCanRetrySkillSetup] = useState(false);
  const [isPollingSkillOperation, setIsPollingSkillOperation] = useState(false);
  const [skillOperationIds, setSkillOperationIds] = useState<
    Partial<Record<string, string>>
  >({});
  const [showProtocolDetails, setShowProtocolDetails] = useState(false);
  const [machineOperationFailureDetails, setMachineOperationFailureDetails] =
    useState<GatewayOperationError | null>(null);
  const [hasNoSkillsIdentified, setHasNoSkillsIdentified] = useState(false);
  const [isAutoGenerateEnabled, setIsAutoGenerateEnabled] = useState(true);
  const [displayUserLoginPasswordAsText, setDisplayUserLoginPasswordAsText] =
    useState(false);
  const [
    displayClientCertificatePasswordAsText,
    setDisplayClientCertificatePasswordAsText,
  ] = useState(false);
  const [opcUaSettings, setOpcUaSettings] = useState(
    createDefaultOpcUaSettings(),
  );

  const texts = getMachineDrawerTexts(i18n);
  const machineSkillsTexts = getMachineSkillsTexts(i18n);

  const isMachineSummaryStep =
    machineOnboardingSummary.status !== "IDLE" && !isRetryEditingSkills;
  const isMachineSkillsStep =
    !isMachineSummaryStep &&
    machineOnboardingStatus ===
      MachineOnboardingStatus.MACHINE_DETAILS_CAPTURED;

  const installedSkillTypes = machineOnboardingSummary.installedSkills;
  const failedSkillTypes = useMemo(
    () => machineOnboardingSummary.failedSkills.map((skill) => skill.type),
    [machineOnboardingSummary.failedSkills],
  );
  const isVncInstalled = installedSkillTypes.includes("VNC");
  const isTwinInstalled = installedSkillTypes.some((type) =>
    ["OPCUA", "RPC"].includes(type),
  );
  const isVncFailed = failedSkillTypes.includes("VNC");
  const isTwinFailed = failedSkillTypes.some((type) =>
    ["OPCUA", "RPC"].includes(type),
  );
  const canUpdateFailedSkills =
    hasSkillOperationFailure &&
    machineOnboardingSummary.failedSkills.length > 0;
  const canRetrySkillOperation =
    machineOnboardingSummary.status === "WARNING" &&
    machineOnboardingSummary.failedSkills.some(
      (skill) => skill.isWarning && Boolean(skillOperationIds[skill.type]),
    );

  const isMachineDetailsCompleted =
    machineAvailabilityStatus === MachineAvailabilityStatus.SUCCESS ||
    isMachineSkillsStep ||
    isMachineSummaryStep;

  const isThingsOsStepCompleted = isMachineSkillsStep || isMachineSummaryStep;

  const steps: { label: string; status: OnboardingStepStatus }[] =
    isMachineDetailsCompleted
      ? [
          { label: texts.stepGateway, status: "completed" },
          { label: texts.stepMachine, status: "completed" },
          {
            label: texts.stepThingsOs,
            status: isThingsOsStepCompleted ? "completed" : "pending",
          },
        ]
      : [
          { label: texts.stepGateway, status: "completed" },
          { label: texts.stepMachine, status: "active" },
          { label: texts.stepThingsOs, status: "pending" },
        ];

  const title =
    isMachineSkillsStep || isMachineSummaryStep
      ? texts.titleStepTwo
      : texts.title;
  const sectionTitle = isMachineSummaryStep
    ? texts.sectionTitleSummary
    : isMachineSkillsStep
    ? texts.sectionTitleStepTwo
    : texts.sectionTitle;

  const isConnectDisabled =
    !selectedGateway || !machineName.trim() || !ipAddress.trim();

  const showConnectButton =
    !areActionsLocked &&
    machineAvailabilityStatus === MachineAvailabilityStatus.IDLE;

  const showContinueButton =
    !areActionsLocked &&
    !isInstallSkillsLoading &&
    machineAvailabilityStatus !== MachineAvailabilityStatus.IDLE &&
    machineAvailabilityStatus !== MachineAvailabilityStatus.FAILED &&
    machineAvailabilityStatus !== MachineAvailabilityStatus.WARNING;

  const showFinishWithoutSkillsButton =
    !areActionsLocked &&
    machineAvailabilityStatus === MachineAvailabilityStatus.SUCCESS;

  const hasAnySupportedMachineSkill = detectedMachineSkills.some(
    (skill) =>
      skill.type === "VNC" || skill.type === "OPCUA" || skill.type === "RPC",
  );

  // Detection no longer gates the journey: the user may continue even when no
  // skill (or no OPC UA) was identified, and configures OPC UA manually.
  const isContinueButtonEnabled =
    machineAvailabilityStatus === MachineAvailabilityStatus.SUCCESS &&
    !isInstallSkillsLoading;

  const isInteractionLocked =
    isInstallSkillsLoading ||
    machineAvailabilityStatus === MachineAvailabilityStatus.LOADING ||
    machineAvailabilityStatus === MachineAvailabilityStatus.IP_REACHABLE;

  const hasStoredGeneratedCertificate = Boolean(
    connectDataSourceClientCertificate,
  );

  const showReuseGeneratedCertificateHint =
    isAutoGenerateEnabled && hasStoredGeneratedCertificate;

  // Keep both protocol tabs visible to match the segmented control design.
  // RPC is disabled for now.
  const twinTypeTabs = useMemo(
    (): Array<"OPCUA" | "RPC"> => ["OPCUA", "RPC"],
    [],
  );
  const hasTwinSkill = twinTypeTabs.length > 0;

  const getDetectedPortForType = useCallback(
    (type: SkillType): string => {
      const detectedSkill = detectedMachineSkills.find(
        (skill) => skill.type === type,
      );
      if (!detectedSkill?.port) {
        return "";
      }

      return `${detectedSkill.port}`;
    },
    [detectedMachineSkills],
  );

  const hasDefaultDetectedPort = (type: SkillType, portValue: string) => {
    const detectedPort = getDetectedPortForType(type);
    return Boolean(detectedPort) && portValue.trim() === detectedPort;
  };

  const showVncPortAvailable =
    isValidPort(vncPort) && hasDefaultDetectedPort("VNC", vncPort);

  useEffect(() => {
    if (isMachineSkillsStep && showVncPortAvailable && !isRetryEditingSkills) {
      setIsSupportExpanded(true);
    }
  }, [isMachineSkillsStep, showVncPortAvailable, isRetryEditingSkills]);

  const showTwinPortAvailable =
    selectedTwinType === "OPCUA" &&
    isValidPort(twinPort) &&
    hasDefaultDetectedPort("OPCUA", twinPort);

  const resetMachineAvailabilityState = useCallback((): void => {
    machineOperationAbortControllerRef.current?.abort();
    machineOperationAbortControllerRef.current = null;
    availabilityRequestGenerationRef.current += 1;
    machineFlowGenerationRef.current += 1;
    setMachineCreateIntent(null);
    setPendingMachineCreateOperationId(null);
    dispatch(setMachineAvailabilityStatus(MachineAvailabilityStatus.IDLE));
    dispatch(setMachineAvailabilityError(""));
    dispatch(setMachineSkillsLabel(""));
    dispatch(
      setMachineOnboardingSummary({
        status: "IDLE",
        installedSkills: [],
        failedSkills: [],
        skippedSkills: [],
      }),
    );
    setCreatedMachineId("");
    setCreatedMachineHostname(null);
    setIsMachineCreated(false);
    setAreActionsLocked(false);
    setIsRetryEditingSkills(false);
    setHasSkillOperationFailure(false);
    setCanRetrySkillSetup(false);
    setIsPollingSkillOperation(false);
    setSkillOperationIds({});
    setShowProtocolDetails(false);
    setMachineOperationFailureDetails(null);
    setHasNoSkillsIdentified(false);
    dispatch(setMachineDetails({}));
    dispatch(setConnectDataSourceClientCertificate(null));
  }, [dispatch]);

  useEffect(() => {
    resetMachineAvailabilityState();
    dispatch(
      setMachineOnboardingStatus(MachineOnboardingStatus.MACHINE_NOT_STARTED),
    );
    setDetectedMachineSkills([]);
    setIsSupportExpanded(false);
    setIsTwinExpanded(false);
  }, [dispatch, resetMachineAvailabilityState]);

  useEffect(() => {
    if (!prefillGateway) {
      setSelectedGateway(null);
      return;
    }

    setSelectedGateway((previousGateway) => {
      if (previousGateway?.gateway_uuid === prefillGateway.gateway_uuid) {
        return previousGateway;
      }
      return prefillGateway;
    });
  }, [prefillGateway]);

  useEffect(() => {
    if (!isMachineSkillsStep) {
      return;
    }

    const initialTwinType = "OPCUA";

    if (isRetryEditingSkills) {
      const installedTwinType = installedSkillTypes.find((type) =>
        ["OPCUA", "RPC"].includes(type),
      );
      const failedTwinType = failedSkillTypes.find((type) =>
        ["OPCUA", "RPC"].includes(type),
      );
      const retryTwinType = failedTwinType || installedTwinType;

      setSelectedTwinType(
        (retryTwinType || initialTwinType) as "OPCUA" | "RPC",
      );
      setIsTwinExpanded(Boolean(retryTwinType));
      setIsSupportExpanded(isVncInstalled || isVncFailed);
      return;
    }

    setSelectedTwinType(initialTwinType);
    setIsSupportExpanded(true);
    setIsTwinExpanded(true);

    const detectedVncPort = getDetectedPortForType("VNC");
    setVncPort((previousPort) => previousPort || detectedVncPort);

    const detectedTwinPort = getDetectedPortForType("OPCUA");
    setTwinPort((previousPort) => previousPort || detectedTwinPort);
  }, [
    isMachineSkillsStep,
    isRetryEditingSkills,
    installedSkillTypes,
    failedSkillTypes,
    isVncInstalled,
    isVncFailed,
    getDetectedPortForType,
  ]);

  const onSelectGateway = (
    gateway: GatewayWithMachinesViewStateTypes | null,
  ): void => {
    availabilityRequestGenerationRef.current += 1;
    setSelectedGateway(gateway);
    resetMachineAvailabilityState();
  };

  const onChangeMachineName = (value: string): void => {
    availabilityRequestGenerationRef.current += 1;
    setMachineName(value);
    resetMachineAvailabilityState();
  };

  const onChangeIpAddress = (value: string): void => {
    const normalizedValue = value.replace(/\s/g, "");
    if (normalizedValue === ipAddress) {
      return;
    }

    availabilityRequestGenerationRef.current += 1;
    setIpAddress(normalizedValue);
    resetMachineAvailabilityState();
  };

  const runMachineAvailabilityCheck = async (): Promise<
    { success: true; machineId?: string } | { success: false }
  > => {
    machineOperationAbortControllerRef.current?.abort();
    machineOperationAbortControllerRef.current = null;
    const requestGeneration = ++availabilityRequestGenerationRef.current;
    const trimmedMachineName = machineName.trim();
    const trimmedIpAddress = ipAddress.trim();

    if (!isValidHostnameOrIpAddress(trimmedIpAddress)) {
      warningSnackbar(texts.invalidIpAddressErrorMessage);
      return { success: false };
    }

    const failMachineAvailability = (
      error: string,
      details?: GatewayOperationError,
    ) => {
      setAreActionsLocked(false);
      setMachineOperationFailureDetails(details || null);
      dispatch(setMachineAvailabilityStatus(MachineAvailabilityStatus.FAILED));
      dispatch(setMachineAvailabilityError(error));
    };

    setAreActionsLocked(false);
    setCreatedMachineId("");
    setCreatedMachineHostname(null);
    setIsMachineCreated(false);
    setDetectedMachineSkills([]);
    dispatch(setMachineDetails({}));
    dispatch(setMachineAvailabilityError(""));
    dispatch(setMachineSkillsLabel(""));
    setMachineOperationFailureDetails(null);
    setShowProtocolDetails(false);
    setHasNoSkillsIdentified(false);
    dispatch(setMachineAvailabilityStatus(MachineAvailabilityStatus.LOADING));

    if (!selectedGateway?.gateway_uuid) {
      failMachineAvailability(texts.machineIpAddressMismatchErrorMessage);
      return { success: false };
    }

    try {
      const machineAvailabilityResult = await checkMachineAvailabilityUseCase(
        selectedGateway.gateway_uuid,
        trimmedIpAddress,
      );

      if (requestGeneration !== availabilityRequestGenerationRef.current) {
        return { success: false };
      }

      const operationId = machineAvailabilityResult.data?.operation_id;
      const machineId = machineAvailabilityResult.data?.machine_id;

      if (!machineAvailabilityResult.success || !operationId) {
        const details = extractGatewayOperationError(
          machineAvailabilityResult.errorData || machineAvailabilityResult.data,
        );

        failMachineAvailability(
          machineAvailabilityResult.error ||
            details?.title ||
            details?.detail ||
            machineAvailabilityResult.data?.detail ||
            machineAvailabilityResult.data?.message ||
            texts.machineIpAddressMismatchErrorMessage,
          details,
        );
        return { success: false };
      }

      const operationAbortController = new AbortController();
      machineOperationAbortControllerRef.current = operationAbortController;

      dispatch(
        setMachineAvailabilityStatus(MachineAvailabilityStatus.IP_REACHABLE),
      );

      if (machineId) {
        setCreatedMachineId(machineId);
        dispatch(
          setMachineDetails({
            machine_id: machineId,
            name: trimmedMachineName,
            hostname: trimmedIpAddress,
          }),
        );
      }

      const machineSkillsResult = await pollMachineAvailabilityOperation(
        operationId,
        texts,
        machineOperationAbortControllerRef.current?.signal,
      );

      if (requestGeneration !== availabilityRequestGenerationRef.current) {
        return { success: false };
      }

      if (machineSkillsResult.success === false) {
        setAreActionsLocked(false);
        setMachineOperationFailureDetails(machineSkillsResult.details || null);
        dispatch(
          setMachineAvailabilityStatus(
            machineSkillsResult.isWarning
              ? MachineAvailabilityStatus.WARNING
              : MachineAvailabilityStatus.FAILED,
          ),
        );
        dispatch(
          setMachineAvailabilityError(
            machineSkillsResult.error || texts.skillsNotAvailableErrorMessage,
          ),
        );
        return { success: false };
      }

      setDetectedMachineSkills(machineSkillsResult.skills);
      setHasNoSkillsIdentified(machineSkillsResult.skills.length === 0);
      dispatch(
        setMachineSkillsLabel(
          machineSkillsResult.skills.map((skill) => skill.type).join(", "),
        ),
      );
      dispatch(setMachineAvailabilityStatus(MachineAvailabilityStatus.SUCCESS));
      return { success: true, machineId };
    } catch {
      if (requestGeneration === availabilityRequestGenerationRef.current) {
        failMachineAvailability(texts.machineIpAddressMismatchErrorMessage);
      }
      return { success: false };
    }
  };

  const handleConnectClick = async (
    e: MouseEvent | KeyboardEvent,
  ): Promise<void> => {
    e.preventDefault();

    await runMachineAvailabilityCheck();
  };

  const handleRetry = async (): Promise<void> => {
    if (machineCreateIntent && pendingMachineCreateOperationId) {
      return runMachineCreateAction(
        machineCreateIntent,
        pendingMachineCreateOperationId,
      );
    }

    if (machineCreateIntent) {
      const availabilityResult = await runMachineAvailabilityCheck();
      if (!availabilityResult.success) {
        return;
      }

      return runMachineCreateAction(machineCreateIntent, undefined, {
        machineId: availabilityResult.machineId,
      });
    }

    // Retry the availability request itself so the backend performs a fresh
    // reachability and skill-detection check before we poll its operation.
    await runMachineAvailabilityCheck();
  };

  const handleContinueToMachineSkills = (
    e: MouseEvent | KeyboardEvent,
  ): Promise<void> => {
    e.preventDefault();

    if (!isContinueButtonEnabled) {
      return Promise.resolve();
    }

    return runMachineCreateAction("continue");
  };

  const toggleAutoGenerateCertificate = (): void => {
    setIsAutoGenerateEnabled((previousState) => {
      const nextState = !previousState;
      setOpcUaSettings((previousSettings) => ({
        ...previousSettings,
        reuseLastAutogeneratedClientCertificate: nextState,
      }));

      return nextState;
    });
  };

  const handlePasswordButton = (e: MouseEvent | KeyboardEvent): void => {
    e.preventDefault();
    setDisplayUserLoginPasswordAsText((previousState) => !previousState);
  };

  const handleClientCertificatePasswordButton = (
    e: MouseEvent | KeyboardEvent,
  ): void => {
    e.preventDefault();
    setDisplayClientCertificatePasswordAsText(
      (previousState) => !previousState,
    );
  };

  const clearOnboardingAfterMachineCompletion = (): void => {
    dispatch(resetOnboardingState());
  };

  const failMachineFlow = (
    error: string,
    skillTypes: string[] = [],
    details?: GatewayOperationError,
  ) => {
    setAreActionsLocked(true);
    setMachineOperationFailureDetails(details || null);
    dispatch(setMachineAvailabilityStatus(MachineAvailabilityStatus.FAILED));
    dispatch(setMachineAvailabilityError(error));

    if (
      machineOnboardingStatus ===
      MachineOnboardingStatus.MACHINE_DETAILS_CAPTURED
    ) {
      // Without a failed entry the summary would render neither the failure
      // message nor the protocol toggle, so an unspecific failure is reported
      // against the machine itself.
      const failedTypes = skillTypes.length > 0 ? skillTypes : ["MACHINE"];

      const installedSkills = isRetryEditingSkills
        ? machineOnboardingSummary.installedSkills
        : [];
      setIsRetryEditingSkills(false);
      setHasSkillOperationFailure(false);
      setCanRetrySkillSetup(true);
      dispatch(
        setMachineOnboardingSummary({
          status: installedSkills.length > 0 ? "PARTIAL_SUCCESS" : "FAILED",
          installedSkills,
          failedSkills: failedTypes
            .filter((type) => !installedSkills.includes(type))
            .map((type) => ({ type, error, details })),
          skippedSkills: [],
        }),
      );
    }
  };

  /** Clears a previous machine-creation failure before a new install attempt. */
  const clearMachineFailureState = (): void => {
    setAreActionsLocked(false);
    setMachineOperationFailureDetails(null);
    setShowProtocolDetails(false);
    dispatch(setMachineAvailabilityError(""));
    dispatch(setMachineAvailabilityStatus(MachineAvailabilityStatus.SUCCESS));
  };

  const failMachineCreation = (
    error: string,
    details?: GatewayOperationError,
    status: MachineAvailabilityStatus = MachineAvailabilityStatus.FAILED,
  ): void => {
    setAreActionsLocked(false);
    setMachineOperationFailureDetails(details || null);
    setShowProtocolDetails(false);
    dispatch(setMachineAvailabilityStatus(status));
    dispatch(setMachineAvailabilityError(error));
  };

  const createOnboardedMachine = async (
    activeMachineFlowGeneration?: number,
    operationIdToResume?: string,
    machineIdOverride?: { machineId?: string },
  ): Promise<{
    machineId: string;
    hostname: string | null;
  } | null> => {
    if (isMachineCreated && createdMachineId) {
      return { machineId: createdMachineId, hostname: createdMachineHostname };
    }

    if (!selectedGateway?.gateway_uuid) {
      const error = texts.createMachineFailedErrorMessage;
      errorSnackbar(error);
      failMachineCreation(error);
      return null;
    }

    let operationId = operationIdToResume;
    if (!operationId) {
      // A fresh availability check is authoritative; don't fall back to a
      // machine ID from the previous create attempt if it returned no ID.
      const createMachineResult =
        await createMachineForMachineOnboardingUseCase({
          gateway_id: selectedGateway.gateway_uuid,
          machine_id: machineIdOverride
            ? machineIdOverride.machineId
            : createdMachineId || undefined,
          name: machineName.trim(),
          hostname: ipAddress.trim(),
          serial_number: null,
        });

      if (
        activeMachineFlowGeneration !== undefined &&
        machineFlowGenerationRef.current !== activeMachineFlowGeneration
      ) {
        return null;
      }

      if (
        !createMachineResult.success ||
        !createMachineResult.data?.operation_id
      ) {
        const details = extractGatewayOperationError(
          createMachineResult.errorData || createMachineResult.data,
        );
        const error =
          createMachineResult.error ||
          details?.title ||
          details?.detail ||
          createMachineResult.data?.detail ||
          createMachineResult.data?.message ||
          texts.createMachineFailedErrorMessage;
        errorSnackbar(error);
        failMachineCreation(error, details);
        return null;
      }

      operationId = createMachineResult.data.operation_id;
    }

    if (!operationId) {
      const error = texts.createMachineFailedErrorMessage;
      errorSnackbar(error);
      failMachineCreation(error);
      return null;
    }

    setPendingMachineCreateOperationId(operationId);
    const operationAbortController = new AbortController();
    machineOperationAbortControllerRef.current?.abort();
    machineOperationAbortControllerRef.current = operationAbortController;

    const operationResult = await pollMachineCreationOperation(
      operationId,
      texts,
      operationAbortController.signal,
    );

    if (
      activeMachineFlowGeneration !== undefined &&
      machineFlowGenerationRef.current !== activeMachineFlowGeneration
    ) {
      return null;
    }

    if (operationResult.success === false) {
      setPendingMachineCreateOperationId(
        operationResult.isWarning ? operationId : null,
      );
      if (!operationResult.isWarning) {
        errorSnackbar(operationResult.error);
      }
      failMachineCreation(
        operationResult.error,
        operationResult.details,
        operationResult.isWarning
          ? MachineAvailabilityStatus.WARNING
          : MachineAvailabilityStatus.FAILED,
      );
      return null;
    }

    const machineId = operationResult.machineId;
    const hostname = ipAddress.trim();
    setPendingMachineCreateOperationId(null);
    setCreatedMachineId(machineId);
    setCreatedMachineHostname(hostname);
    setIsMachineCreated(true);
    dispatch(setMachineAvailabilityError(""));
    dispatch(
      setMachineDetails({
        machine_id: machineId,
        name: machineName.trim(),
        hostname,
      }),
    );

    return { machineId, hostname };
  };

  const runMachineCreateAction = async (
    intent: "continue" | "finish",
    operationIdToResume?: string,
    machineIdOverride?: { machineId?: string },
  ): Promise<void> => {
    const activeMachineFlowGeneration = ++machineFlowGenerationRef.current;
    setMachineCreateIntent(intent);
    setAreActionsLocked(true);
    setIsInstallSkillsLoading(true);
    setMachineOperationFailureDetails(null);
    dispatch(setMachineAvailabilityError(""));
    dispatch(setMachineAvailabilityStatus(MachineAvailabilityStatus.LOADING));

    try {
      const createdMachine = await createOnboardedMachine(
        activeMachineFlowGeneration,
        operationIdToResume,
        machineIdOverride,
      );

      if (
        machineFlowGenerationRef.current !== activeMachineFlowGeneration ||
        !createdMachine
      ) {
        return;
      }

      setAreActionsLocked(false);
      setMachineCreateIntent(null);
      setPendingMachineCreateOperationId(null);
      dispatch(setMachineAvailabilityStatus(MachineAvailabilityStatus.SUCCESS));
      if (selectedGateway) {
        dispatch(
          setGatewayDetails({
            gateway_id: selectedGateway.gateway_uuid,
            name: selectedGateway.name,
          }),
        );
      }

      if (intent === "continue") {
        dispatch(
          setMachineOnboardingStatus(
            MachineOnboardingStatus.MACHINE_DETAILS_CAPTURED,
          ),
        );
      } else {
        dispatch(
          setMachineOnboardingSummary({
            status: "SKIPPED",
            installedSkills: [],
            failedSkills: [],
            skippedSkills: detectedMachineSkills.map((skill) => skill.type),
          }),
        );
      }
    } catch {
      if (machineFlowGenerationRef.current === activeMachineFlowGeneration) {
        const error = texts.createMachineFailedErrorMessage;
        errorSnackbar(error);
        failMachineCreation(error);
      }
    } finally {
      if (machineFlowGenerationRef.current === activeMachineFlowGeneration) {
        setIsInstallSkillsLoading(false);
      }
    }
  };

  // Reuse a generated certificate while auto-generation remains enabled;
  // disabling auto-generation clears it so manual certificate settings are used.
  useEffect(() => {
    if (!isAutoGenerateEnabled) {
      dispatch(setConnectDataSourceClientCertificate(null));
    }
  }, [isAutoGenerateEnabled, dispatch]);

  const handleInstallAndFinishSetup = async (): Promise<void> => {
    const hostName = ipAddress.trim();
    const previousInstalledSkills = isRetryEditingSkills
      ? machineOnboardingSummary.installedSkills
      : [];
    const payloads: CreateSkillRequestBody[] = [];
    let submittedOpcUaEndpoint: string | undefined;
    const shouldInstallVnc =
      !isVncInstalled &&
      (isRetryEditingSkills
        ? isVncFailed || isSupportExpanded
        : isSupportExpanded);
    const shouldInstallTwin =
      !isTwinInstalled &&
      hasTwinSkill &&
      Boolean(selectedTwinType) &&
      (isRetryEditingSkills ? isTwinFailed || isTwinExpanded : isTwinExpanded);
    const isOpcUaSelected = shouldInstallTwin && selectedTwinType === "OPCUA";
    const useAutoGeneratedClientCertificate =
      isOpcUaSelected && isAutoGenerateEnabled;

    if (shouldInstallVnc) {
      if (!isValidPort(vncPort)) {
        warningSnackbar(texts.invalidPortErrorMessage);
        return;
      }

      payloads.push(
        buildSkillPayload("VNC", hostName, opcUaSettings, Number(vncPort)),
      );
    }

    if (shouldInstallTwin && selectedTwinType) {
      if (selectedTwinType === "OPCUA") {
        if (!isValidPort(twinPort)) {
          warningSnackbar(texts.invalidPortErrorMessage);
          return;
        }

        if (!isAutoGenerateEnabled) {
          const missingFields: string[] = [];
          if (!opcUaSettings.clientCertificateBase64) {
            missingFields.push(machineSkillsTexts.clientCertificateLabel);
          }
          if (!opcUaSettings.clientCertificatePrivateKeyBase64) {
            missingFields.push(
              machineSkillsTexts.clientCertificatePrivateKeyLabel,
            );
          }

          if (missingFields.length > 0) {
            warningSnackbar(
              `${machineSkillsTexts.pleaseFixFieldsLabel}: ${missingFields.join(
                ", ",
              )}`,
              5000,
            );
            return;
          }
        }

        // The OPCUA payload is built after machine creation, once the
        // client certificate (generated or stored) is available.
      } else {
        const trimmedRpcDsn = rpcDsn.trim();
        if (!isValidIpAddress(trimmedRpcDsn)) {
          warningSnackbar(texts.invalidRpcDsnErrorMessage);
          return;
        }

        payloads.push(
          buildSkillPayload(
            "RPC",
            hostName,
            opcUaSettings,
            undefined,
            trimmedRpcDsn,
          ),
        );
      }
    }

    clearMachineFailureState();
    setHasSkillOperationFailure(false);
    setCanRetrySkillSetup(false);

    const selectedSkillTypes: string[] = [
      ...(shouldInstallVnc ? ["VNC"] : []),
      ...(shouldInstallTwin && selectedTwinType ? [selectedTwinType] : []),
    ];

    const activeMachineFlowGeneration = ++machineFlowGenerationRef.current;
    setIsInstallSkillsLoading(true);

    try {
      const createdMachine = await createOnboardedMachine(
        activeMachineFlowGeneration,
      );

      if (
        machineFlowGenerationRef.current !== activeMachineFlowGeneration ||
        !createdMachine
      ) {
        return;
      }

      const { machineId, hostname } = createdMachine;

      if (isOpcUaSelected) {
        let generatedCertificate = useAutoGeneratedClientCertificate
          ? connectDataSourceClientCertificate
          : null;

        if (useAutoGeneratedClientCertificate && !generatedCertificate) {
          const certificateResult = await generateOpcUaClientCertificateUseCase(
            hostname ? [hostname] : [],
          );

          if (
            machineFlowGenerationRef.current !== activeMachineFlowGeneration
          ) {
            return;
          }

          if (
            !certificateResult.success ||
            !certificateResult.data?.clientCertificateBase64.trim() ||
            !certificateResult.data.clientCertificatePrivateKeyBase64.trim()
          ) {
            const certificateError =
              certificateResult.error ||
              texts.certificateGenerationFailedErrorMessage;
            errorSnackbar(certificateError);
            failMachineFlow(
              certificateError,
              selectedSkillTypes,
              extractGatewayOperationError(certificateResult.errorData),
            );
            return;
          }

          generatedCertificate = certificateResult.data;
          dispatch(setConnectDataSourceClientCertificate(generatedCertificate));
        }

        const effectiveOpcUaSettings = generatedCertificate
          ? {
              ...opcUaSettings,
              clientCertificateBase64:
                generatedCertificate.clientCertificateBase64,
              clientCertificatePrivateKeyBase64:
                generatedCertificate.clientCertificatePrivateKeyBase64,
              clientCertificatePassword:
                generatedCertificate.clientCertificatePassword ?? "",
            }
          : opcUaSettings;

        const opcUaPayload = buildSkillPayload(
          "OPCUA",
          hostName,
          effectiveOpcUaSettings,
          Number(twinPort),
        );
        if (opcUaPayload.type === "OPCUA") {
          submittedOpcUaEndpoint = opcUaPayload.connection_details.dsn;
        }
        payloads.push(opcUaPayload);
      }

      if (payloads.length === 0) {
        dispatch(
          setMachineOnboardingSummary({
            status: previousInstalledSkills.length > 0 ? "SUCCESS" : "SKIPPED",
            installedSkills: previousInstalledSkills,
            failedSkills: [],
            skippedSkills:
              previousInstalledSkills.length > 0
                ? []
                : detectedMachineSkills.map((skill) => skill.type),
          }),
        );
        setIsRetryEditingSkills(false);
        return;
      }

      const skillOperationAbortController = new AbortController();
      machineOperationAbortControllerRef.current?.abort();
      machineOperationAbortControllerRef.current =
        skillOperationAbortController;
      const createSkillResult = await postCreateSkillUseCase(
        payloads,
        machineId,
        texts.operationPendingWarningMessage,
        skillOperationAbortController.signal,
      );

      if (machineFlowGenerationRef.current !== activeMachineFlowGeneration) {
        return;
      }

      if (!createSkillResult.success) {
        errorSnackbar(
          createSkillResult.error || texts.skillsNotAvailableErrorMessage,
        );
        failMachineFlow(
          createSkillResult.error || texts.skillsNotAvailableErrorMessage,
          selectedSkillTypes,
          createSkillResult.errorDetails,
        );
        return;
      }

      setSkillOperationIds(createSkillResult.operationIdsBySkillType || {});
      const successfulSkillTypes = createSkillResult.successfulSkillTypes || [];
      const opcUaOperation =
        createSkillResult.operationResultsBySkillType?.OPCUA;
      const opcUaTimedOutAtIdle = createSkillResult.failedSkillTypes?.some(
        (failedSkill) => failedSkill.type === "OPCUA" && failedSkill.isWarning,
      );
      const isOpcUaIdle =
        selectedSkillTypes.includes("OPCUA") &&
        opcUaTimedOutAtIdle &&
        getOperationState(opcUaOperation) === "IDLE";
      const existingOpcUaErrorDetails =
        createSkillResult.failedSkillTypes?.find(
          (failedSkill) => failedSkill.type === "OPCUA",
        )?.details;
      const opcUaIdleErrorDetails: GatewayOperationError | undefined =
        isOpcUaIdle
          ? {
              ...(existingOpcUaErrorDetails || {}),
              error_code: "OPCUA_CONNECTION_SETUP_IDLE",
              title: texts.opcUaConnectionSetupIdleTitle,
              detail: texts.opcUaConnectionSetupIdleDetail.replace(
                "{endpoint}",
                submittedOpcUaEndpoint ||
                  `opc.tcp://${hostName}:${Number(twinPort)}`,
              ),
              severity: "ERROR",
              recommended_action:
                texts.opcUaConnectionSetupIdleRecommendedAction,
            }
          : undefined;
      const machineSourceId = successfulSkillTypes.includes("OPCUA")
        ? opcUaOperation?.result?.source_id || opcUaOperation?.source_id
        : undefined;

      if (machineSourceId) {
        dispatch(
          setMachineDetails({
            machine_id: machineId,
            source_id: machineSourceId,
            name: machineName,
            hostname: hostName,
          }),
        );
      }

      const installedSkills = Array.from(
        new Set([
          ...previousInstalledSkills,
          ...selectedSkillTypes.filter((type) =>
            successfulSkillTypes.includes(type),
          ),
        ]),
      );

      const failedSkillResults: MachineOnboardingSummary["failedSkills"] =
        createSkillResult.failedSkillTypes &&
        createSkillResult.failedSkillTypes.length > 0
          ? createSkillResult.failedSkillTypes
          : selectedSkillTypes
              .filter((type) => !successfulSkillTypes.includes(type))
              .map((type) => ({
                type,
                error: texts.skillsNotAvailableErrorMessage,
              }));
      const failedSkills = failedSkillResults.map((failedSkill) =>
        failedSkill.type === "OPCUA" && opcUaIdleErrorDetails
          ? { ...failedSkill, details: opcUaIdleErrorDetails }
          : failedSkill,
      );

      if (
        createSkillResult.isWarning &&
        failedSkills.some(
          (failedSkill) =>
            failedSkill.type === "OPCUA" && Boolean(failedSkill.details),
        )
      ) {
        setShowProtocolDetails(true);
      }

      if (failedSkills.length > 0) {
        setIsRetryEditingSkills(false);
        setHasSkillOperationFailure(!createSkillResult.isWarning);
        setCanRetrySkillSetup(false);
        // Keep the operation protocol of the failure available to the summary
        // panel and the availability panel after a retry.
        setMachineOperationFailureDetails(failedSkills[0].details || null);
        dispatch(
          setMachineOnboardingSummary({
            status: createSkillResult.isWarning
              ? "WARNING"
              : installedSkills.length > 0
              ? "PARTIAL_SUCCESS"
              : "FAILED",
            installedSkills,
            failedSkills,
            skippedSkills: [],
          }),
        );

        errorSnackbar(failedSkills[0].error);
        return;
      }

      setIsRetryEditingSkills(false);
      setHasSkillOperationFailure(false);
      setCanRetrySkillSetup(false);
      dispatch(
        setMachineOnboardingSummary({
          status: "SUCCESS",
          installedSkills,
          failedSkills: [],
          skippedSkills: [],
        }),
      );
      successSnackbar(texts.skillInstallSuccessMessage);
    } catch {
      if (machineFlowGenerationRef.current === activeMachineFlowGeneration) {
        failMachineFlow(
          texts.skillsNotAvailableErrorMessage,
          selectedSkillTypes,
        );
      }
    } finally {
      if (machineFlowGenerationRef.current === activeMachineFlowGeneration) {
        setIsInstallSkillsLoading(false);
      }
    }
  };

  const handleFinishWithoutMachineSkills = async (): Promise<void> => {
    if (isMachineCreated && createdMachineId) {
      dispatch(
        setMachineOnboardingSummary({
          status: "SKIPPED",
          installedSkills: [],
          failedSkills: [],
          skippedSkills: detectedMachineSkills.map((skill) => skill.type),
        }),
      );
      return;
    }

    return runMachineCreateAction("finish");
  };

  const resetMachineDrawerLocalState = (
    preserveSelectedGateway = false,
  ): void => {
    machineOperationAbortControllerRef.current?.abort();
    machineOperationAbortControllerRef.current = null;
    machineFlowGenerationRef.current += 1;
    dispatch(
      setMachineOnboardingStatus(MachineOnboardingStatus.MACHINE_NOT_STARTED),
    );
    resetMachineAvailabilityState();

    setIsInstallSkillsLoading(false);
    if (!preserveSelectedGateway) {
      setSelectedGateway(null);
    }
    setMachineName("");
    setIpAddress("");
    setDetectedMachineSkills([]);
    setIsSupportExpanded(false);
    setIsTwinExpanded(false);
    setVncPort("");
    setTwinPort("");
    setRpcDsn("");
    setSelectedTwinType(null);
    setIsAutoGenerateEnabled(true);
    setDisplayUserLoginPasswordAsText(false);
    setDisplayClientCertificatePasswordAsText(false);
    setOpcUaSettings(createDefaultOpcUaSettings());
  };

  const handleFinishSummaryStep = (): void => {
    const selectedGatewayMatchesOnboardingGateway =
      selectedGateway?.gateway_uuid === onboardingGateway.gateway_id;
    const gatewayToPreserve = selectedGateway
      ? selectedGatewayMatchesOnboardingGateway
        ? onboardingGateway
        : {
            gateway_id: selectedGateway.gateway_uuid,
            name: selectedGateway.name,
            vendor_id: selectedGateway.vendor_id,
          }
      : onboardingGateway;
    const shouldRestoreCompletedGatewayStatus =
      gatewayOnboardingStatus ===
        GatewayOnboardingStatus.GATEWAY_ONBOARDING_COMPLETED &&
      selectedGateway?.gateway_uuid === completedGatewayIdRef.current;

    clearOnboardingAfterMachineCompletion();
    if (gatewayToPreserve.gateway_id) {
      dispatch(setGatewayDetails(gatewayToPreserve));
      if (shouldRestoreCompletedGatewayStatus) {
        dispatch(
          setGatewayOnboardingStatus(
            GatewayOnboardingStatus.GATEWAY_ONBOARDING_COMPLETED,
          ),
        );
      }
    }
    resetMachineDrawerLocalState(true);
  };

  const handleGoToNewMachine = (): void => {
    const machineId = onboardingMachine?.machine_id;

    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "addNewMachineDrawerModal",
      }),
    );
    clearOnboardingAfterMachineCompletion();
    resetMachineDrawerLocalState();
    navigateTo(
      machineId
        ? `${Route.MACHINES}?machine_id=${encodeURIComponent(machineId)}`
        : Route.MACHINES,
    );
  };

  const handleRetrySkillOperation = async (): Promise<void> => {
    const pendingSkill = machineOnboardingSummary.failedSkills.find(
      (skill) => skill.isWarning && skillOperationIds[skill.type],
    );
    const operationId = pendingSkill
      ? skillOperationIds[pendingSkill.type]
      : undefined;

    if (!pendingSkill || !operationId || isPollingSkillOperation) {
      return;
    }

    setShowProtocolDetails(false);
    setIsPollingSkillOperation(true);
    machineOperationAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    machineOperationAbortControllerRef.current = abortController;
    const activeGeneration = ++machineFlowGenerationRef.current;

    try {
      const pollResult = await pollSkillOperationUseCase(
        operationId,
        texts.operationPendingWarningMessage,
        abortController.signal,
      );

      if (machineFlowGenerationRef.current !== activeGeneration) {
        return;
      }

      const operationState = getOperationState(pollResult.operation);
      const isIdleOpcUa =
        pendingSkill.type === "OPCUA" &&
        pollResult.status === "warning" &&
        operationState === "IDLE";
      const details = isIdleOpcUa
        ? {
            ...(pollResult.details || pendingSkill.details || {}),
            error_code: "OPCUA_CONNECTION_SETUP_IDLE",
            title: texts.opcUaConnectionSetupIdleTitle,
            detail: texts.opcUaConnectionSetupIdleDetail.replace(
              "{endpoint}",
              `opc.tcp://${ipAddress.trim()}:${Number(twinPort)}`,
            ),
            severity: "ERROR",
            recommended_action: texts.opcUaConnectionSetupIdleRecommendedAction,
          }
        : pollResult.details;

      if (pollResult.status === "success") {
        setHasSkillOperationFailure(false);
        setCanRetrySkillSetup(false);
        const installedSkills = Array.from(
          new Set([
            ...machineOnboardingSummary.installedSkills,
            pendingSkill.type,
          ]),
        );
        const failedSkills = machineOnboardingSummary.failedSkills.filter(
          (skill) => skill.type !== pendingSkill.type,
        );
        dispatch(
          setMachineOnboardingSummary({
            status: failedSkills.length > 0 ? "PARTIAL_SUCCESS" : "SUCCESS",
            installedSkills,
            failedSkills,
            skippedSkills: [],
          }),
        );
        const opcUaSourceId =
          pendingSkill.type === "OPCUA"
            ? pollResult.operation?.result?.source_id ||
              pollResult.operation?.source_id
            : undefined;
        if (opcUaSourceId) {
          dispatch(
            setMachineDetails({
              machine_id: createdMachineId,
              source_id: opcUaSourceId,
              name: machineName,
              hostname: ipAddress,
            }),
          );
        }
        setMachineOperationFailureDetails(failedSkills[0]?.details || null);
        setShowProtocolDetails(false);
        return;
      }

      setHasSkillOperationFailure(pollResult.status === "error");
      setCanRetrySkillSetup(false);
      const updatedFailedSkills = machineOnboardingSummary.failedSkills.map(
        (skill) =>
          skill.type === pendingSkill.type
            ? {
                ...skill,
                error: pollResult.error || skill.error,
                details,
                isWarning: pollResult.status === "warning",
              }
            : skill,
      );
      const hasPendingOperations = updatedFailedSkills.some(
        (skill) => skill.isWarning,
      );
      const status = hasPendingOperations
        ? "WARNING"
        : machineOnboardingSummary.installedSkills.length > 0
        ? "PARTIAL_SUCCESS"
        : "FAILED";

      dispatch(
        setMachineOnboardingSummary({
          status,
          installedSkills: machineOnboardingSummary.installedSkills,
          failedSkills: updatedFailedSkills,
          skippedSkills: [],
        }),
      );
      setMachineOperationFailureDetails(details || null);
      setShowProtocolDetails(
        pendingSkill.type === "OPCUA" &&
          pollResult.status === "warning" &&
          Boolean(details),
      );
    } catch {
      if (machineFlowGenerationRef.current === activeGeneration) {
        setShowProtocolDetails(false);
      }
    } finally {
      if (machineFlowGenerationRef.current === activeGeneration) {
        setIsPollingSkillOperation(false);
      }
    }
  };

  const handleUpdatePortsFromSummary = (): void => {
    if (!canUpdateFailedSkills && !canRetrySkillSetup) {
      return;
    }
    setAreActionsLocked(false);
    setShowProtocolDetails(false);
    setHasSkillOperationFailure(false);
    setCanRetrySkillSetup(false);
    setIsRetryEditingSkills(true);
  };

  const handleShowProtocol = () => {
    setShowProtocolDetails((currentValue) => !currentValue);
  };

  const handleGetSupport = () => {
    const help = new Help(new HelpRepository());
    const gatewayUuid =
      selectedGateway?.gateway_uuid || onboardingGateway?.gateway_id;
    const machineUuid = onboardingMachine?.machine_id || createdMachineId;
    const sourceId = onboardingMachine?.source_id;

    const failedSkillErrors = machineOnboardingSummary.failedSkills
      .map((skill) => `${skill.type}: ${skill.error}`)
      .join("; ");

    window.open(
      help.getContactUrlForMachineOnboarding({
        gatewayName: selectedGateway?.name || onboardingGateway?.name,
        gatewayUuid,
        namespaceName: onboardingGateway?.namespace,
        machineName,
        machineUuid,
        sourceId,
        hostname: ipAddress,
        failureStage: isMachineCreated
          ? "Skill installation"
          : "Machine availability or creation",
        failedSkill:
          machineOnboardingSummary.failedSkills.length > 0
            ? machineOnboardingSummary.failedSkills
                .map((skill) => skill.type)
                .join(", ")
            : undefined,
        errorMessage: failedSkillErrors || machineAvailabilityError,
        errorDetails: machineOperationFailureDetails
          ? {
              "Error code": machineOperationFailureDetails.error_code,
              Title: machineOperationFailureDetails.title,
              Detail: machineOperationFailureDetails.detail,
              Severity: machineOperationFailureDetails.severity,
              "Recommended action":
                machineOperationFailureDetails.recommended_action,
              "Trace ID": machineOperationFailureDetails.trace_id,
            }
          : undefined,
      }),
      "_blank",
    );
  };

  const handleCloseMachineDrawer = (): void => {
    clearOnboardingAfterMachineCompletion();
    resetMachineDrawerLocalState();
  };

  return {
    texts,
    machineSkillsTexts,
    detectedMachineSkillTypes: detectedMachineSkills
      .map((skill) => skill.type)
      .join(", "),
    title,
    sectionTitle,
    steps,
    isMachineSkillsStep,
    isMachineSummaryStep,
    machineOnboardingSummary,
    isRetryEditingSkills,
    installedSkillTypes,
    isVncInstalled,
    isTwinInstalled,
    isVncFailed,
    isTwinFailed,
    canRetrySkillOperation,
    canUpdateFailedSkills,
    canRetrySkillSetup,
    isPollingSkillOperation,
    onboardingMachine,
    connectedGatewaysListData,
    selectedGateway,
    onSelectGateway,
    machineName,
    onChangeMachineName,
    ipAddress,
    onChangeIpAddress,
    isConnectDisabled,
    showConnectButton,
    showContinueButton,
    showFinishWithoutSkillsButton,
    isContinueButtonEnabled,
    machineAvailabilityStatus,
    isInteractionLocked,
    machineSkillsLabel,
    machineAvailabilityError,
    machineOperationFailureDetails,
    hasNoSkillsIdentified,
    hasAnySupportedMachineSkill,
    showProtocolDetails,
    areActionsLocked,
    hasTwinSkill,
    twinTypeTabs,
    isSupportExpanded,
    isTwinExpanded,
    vncPort,
    twinPort,
    rpcDsn,
    selectedTwinType,
    opcUaSettings,
    isAutoGenerateEnabled,
    showReuseGeneratedCertificateHint,
    hasStoredGeneratedCertificate,
    displayUserLoginPasswordAsText,
    displayClientCertificatePasswordAsText,
    isInstallSkillsLoading,
    showVncPortAvailable,
    showTwinPortAvailable,
    setIsSupportExpanded,
    setIsTwinExpanded,
    setVncPort,
    setTwinPort,
    setRpcDsn,
    setSelectedTwinType,
    setOpcUaSettings,
    toggleAutoGenerateCertificate,
    handlePasswordButton,
    handleClientCertificatePasswordButton,
    handleConnectClick,
    handleRetry,
    handleContinueToMachineSkills,
    handleInstallAndFinishSetup,
    handleFinishWithoutMachineSkills,
    handleFinishSummaryStep,
    handleGoToNewMachine,
    handleUpdatePortsFromSummary,
    handleRetrySkillOperation,
    handleShowProtocol,
    handleGetSupport,
    handleCloseMachineDrawer,
    isGatewayPrefilled:
      prefillGateway !== null &&
      gatewayOnboardingStatus ===
        GatewayOnboardingStatus.GATEWAY_ONBOARDING_COMPLETED,
    isMachineDetailsCompleted,
    isThingsOsStepCompleted,
  };
};
